import type { RaRecord } from "react-admin";

import {
  formatNarrationIts,
  formatNarrationRupee,
  formatNarrationTransactionId,
} from "./receipt-narration-format";

export type LagatReceiptNarrationInput = {
  itsNo?: unknown;
  name?: unknown;
  amount?: unknown;
  paymentMode?: unknown;
  paymentRef?: unknown;
  transactionId?: unknown;
  /** Booking purpose display name when resolved by API */
  purposeName?: unknown;
  /** Booking purpose id fallback */
  purpose?: unknown;
};

export type LagatReceiptNarrationOptions = {
  includeItsNo?: boolean;
  includeName?: boolean;
  includeAmount?: boolean;
  includePaymentMode?: boolean;
  includeTransactionId?: boolean;
  includePurpose?: boolean;
};

function parseAmount(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

function resolvePurpose(input: LagatReceiptNarrationInput): string {
  const label = String(input.purposeName ?? input.purpose ?? "").trim();
  return label || "Voluntary Contribution";
}

/** ITS, name, paid amount, payment mode, txn id, purpose — Tally export (not printed on receipt). */
export function formatLagatReceiptNarration(
  input: LagatReceiptNarrationInput,
  options?: LagatReceiptNarrationOptions
): string {
  const includeItsNo = options?.includeItsNo ?? true;
  const includeName = options?.includeName ?? true;
  const includeAmount = options?.includeAmount ?? true;
  const includePaymentMode = options?.includePaymentMode ?? true;
  const includeTransactionId = options?.includeTransactionId ?? true;
  const includePurpose = options?.includePurpose ?? true;

  const itsPart = includeItsNo ? formatNarrationIts(input.itsNo) : "";
  const name = includeName ? String(input.name ?? "").trim() : "";
  const paidAmount = includeAmount ? formatNarrationRupee(parseAmount(input.amount)) : "";
  const paymentMode = includePaymentMode
    ? String(input.paymentMode ?? "")
        .trim()
        .toUpperCase()
    : "";
  const txnPart = includeTransactionId
    ? formatNarrationTransactionId(input.transactionId ?? input.paymentRef)
    : "";
  const purpose = includePurpose ? resolvePurpose(input) : "";

  return [itsPart, name, paidAmount, paymentMode, txnPart, purpose]
    .filter((part) => part !== "")
    .join(", ");
}

export function formatLagatReceiptNarrationFromRecord(
  rec: RaRecord,
  options?: LagatReceiptNarrationOptions
): string {
  return formatLagatReceiptNarration(
    {
      itsNo: rec.itsNo,
      name: rec.name,
      amount: rec.amount,
      paymentMode: rec.paymentMode,
      paymentRef: rec.paymentRef,
      transactionId: rec.transactionId,
      purposeName: rec.purposeName,
      purpose: rec.purpose,
    },
    options
  );
}
